import type { ClinicResponse } from '@/api/hospital';

/**
 * Per-clinic custom column definitions. v1 ships them as a JSON string on
 * `clinic.flags.hospital_patient_custom_fields_json` so each hospital can
 * decide what shows up on the patient list without a schema change.
 *
 * Each entry has at minimum:
 *   - `label` — column header shown to the clinician
 *   - `field` — the key under `patient.hospital_attributes.patient_custom_fields`
 *     where the row's value lives
 *
 * Optional `values` is a dict of allowed enum entries (used by the
 * Add/Edit Patient forms for select-style inputs); the list view doesn't
 * use it.
 */
export type HospitalPatientCustomField = {
  label: string;
  field: string;
  values?: Record<
    string,
    { label: string; value: string; description?: string }
  >;
};

/**
 * Parse the JSON-string flag on a single clinic. Returns null when the flag
 * is unset, empty, or malformed — `console.error` so a clinic admin who
 * mis-formats their JSON sees a useful error in dev tools without breaking
 * the page.
 */
export const clinicCustomFields = (
  clinic: ClinicResponse | null | undefined,
): HospitalPatientCustomField[] | null => {
  const raw = clinic?.flags?.hospital_patient_custom_fields_json;
  if (!raw) return null;
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (e): e is HospitalPatientCustomField =>
        !!e && typeof e === 'object' && typeof (e as HospitalPatientCustomField).field === 'string',
    );
  } catch (e) {
    // Bad JSON in a clinic flag shouldn't crash the patient list — log and
    // skip just that clinic's fields.
    console.error('Failed to parse hospital_patient_custom_fields_json', e);
    return null;
  }
};

/**
 * The definitions a clinic declares, ready to render as FORM inputs.
 *
 * Narrower than `clinicCustomFields`: an entry with no `label` is dropped,
 * because an unlabelled input is not usable. The patient list keeps the
 * looser reading — it can show a column with a blank header.
 *
 * Shared by the Add and Edit Patient dialogs so the two cannot drift.
 */
export const clinicCustomFieldInputs = (
  clinic: ClinicResponse | null | undefined,
): HospitalPatientCustomField[] =>
  (clinicCustomFields(clinic) ?? []).filter(
    (d) => typeof d.label === 'string' && d.label !== '',
  );

/**
 * Union the custom-field definitions from every clinic the clinician
 * belongs to. Dedupes by `field` (so two clinics declaring the same
 * `external_chart_id` field produce one column, taking the first clinic's
 * label). Result order is the order fields were first seen — stable
 * across renders given the same input.
 */
export const unionClinicCustomFields = (
  clinics: ClinicResponse[] | null | undefined,
): HospitalPatientCustomField[] => {
  if (!clinics || clinics.length === 0) return [];
  const byField = new Map<string, HospitalPatientCustomField>();
  for (const clinic of clinics) {
    const fields = clinicCustomFields(clinic);
    if (!fields) continue;
    for (const f of fields) {
      if (!byField.has(f.field)) byField.set(f.field, f);
    }
  }
  return Array.from(byField.values());
};

/**
 * True when any of the clinician's clinics carries the
 * `hospital_patient_has_external_id` flag. Drives the "External ID"
 * column visibility — separate flag from custom fields because v1 treats
 * the external id as a first-class field, not a "custom" one.
 */
export const anyClinicHasExternalId = (
  clinics: ClinicResponse[] | null | undefined,
): boolean => {
  if (!clinics) return false;
  return clinics.some((c) => c.flags?.hospital_patient_has_external_id === true);
};
